import React from 'react'
import { useTranslation } from 'react-i18next'

interface Table {
  id: number
  name: string
  status: 'available' | 'occupied' | 'reserved'
  capacity?: number
  location?: string
  isVIP?: boolean
}

interface TableCardProps {
  table: Table
  isSelected: boolean
  isSelectable: boolean
  onSelect: () => void
  isRTL?: boolean
}

const TableCard: React.FC<TableCardProps> = ({
  table,
  isSelected,
  isSelectable,
  onSelect,
  isRTL = false
}) => {
  const { t } = useTranslation()

  const getTableColor = () => {
    if (isSelected) {
      return 'bg-yellow-400' // Yellow color for selected tables (like in the image)
    }
    // Always gray for table card background
    return 'bg-gray-200' // Gray color for all non-selected tables
  }

  const getCircleColor = () => {
    if (isSelected) {
      // If selected and occupied, keep red circle. Otherwise yellow circle
      if (table.status === 'occupied') {
        return 'bg-red-600' // Red circle for selected occupied tables
      }
      return 'bg-yellow-400' // Yellow circle for selected available tables
    }
    switch (table.status) {
      case 'occupied':
        return 'bg-red-600' // Red circle for busy/occupied
      case 'reserved':
        return 'bg-white' // White for reserved
      default:
        return 'bg-blue-600' // Blue circle for available tables (like in legend)
    }
  }

  const getTextColor = () => {
    // If occupied, always white text (even if selected)
    if (table.status === 'occupied') {
      return 'text-white' // White text for occupied tables
    }
    // If selected and available, red text
    if (isSelected) {
      return 'text-red-600' // Red text for selected available tables
    }
    // If available and not selected, white text (because circle is blue)
    return 'text-white' // White text for available tables (blue circle)
  }

  const getStatusText = () => {
    switch (table.status) {
      case 'occupied':
        return t('table.occupied')
      case 'reserved':
        return t('table.reserved')
      default:
        return t('table.available')
    }
  }

  const canSelect = isSelectable

  const handleClick = () => {
    if (canSelect) {
      onSelect()
    }
  }

  const tableColor = getTableColor()
  const circleColor = getCircleColor()
  const textColor = getTextColor()
  const isVipTable = Boolean(table.isVIP)

  return (
    <div
      onClick={handleClick}
      className={`
        inline-flex flex-col justify-start items-start gap-1.5
        cursor-pointer transition-all duration-200
        ${canSelect ? '' : 'cursor-not-allowed opacity-60'}
      `}
    >
      {/* Top chairs row */}
      <div className="self-stretch inline-flex justify-start items-center gap-1">
        <div className={`flex-1  h-3 ${tableColor} rounded-2xl`} />
        <div className={`flex-1 h-3 ${tableColor} rounded-2xl`} />
      </div>

      {/* Main table area */}
      <div className={` w-full h-[80px] relative ${tableColor} rounded-xl inline-flex justify-center items-center gap-2.5`}>
        {/* VIP badge - Hidden */}
        {/* {isVipTable && (
          <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-semibold leading-none tracking-wide text-white bg-[#f6b53d] rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.4)]">
            VIP
          </span>
        )} */}
        <div className={`w-[49px] h-[49px] px-1 ${circleColor} rounded-full flex justify-center items-center gap-2 `}>
          <div className={`text-center justify-center text-[10px] font-medium font-['IBM Plex Sans Arabic'] leading-tight ${isRTL ? 'text-right' : 'text-left'} ${textColor}`}>
            {table.name}
          </div>
        </div>
      </div>

      {/* Bottom chairs row */}
      <div className="self-stretch inline-flex justify-start items-center gap-1">
        <div className={`flex-1 h-3 ${tableColor} rounded-2xl`} />
        <div className={`flex-1 h-3 ${tableColor} rounded-2xl`} />
      </div>
    </div>
  )
}

export default TableCard
